import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { reportService } from '../../services/api';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [loading, setLoading] = useState(true);
  const [counts, setCounts] = useState({
    totalRecords: 0,
    activeProjects: 0,
    pendingTenders: 0,
    activeQuotations: 0,
    stockItems: 0,
    organizations: 0
  });

  const [barChartData, setBarChartData] = useState({
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
    records: [0, 0, 0, 0, 0, 0],
    projects: [0, 0, 0, 0, 0, 0]
  });

  const [tenderStatusData, setTenderStatusData] = useState({
    labels: ['Draft', 'Submitted', 'Evaluated', 'Awarded', 'Not Awarded', 'Cancelled'],
    data: [0, 0, 0, 0, 0, 0]
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await reportService.getStats();
      const data = response.data;

      if (data.counts) setCounts(data.counts);
      if (data.chartData) setBarChartData(data.chartData);
      if (data.tenderStats) {
        setTenderStatusData({
          labels: data.tenderStats.labels,
          data: data.tenderStats.data
        });
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard stats:", error);
      setLoading(false);
    }
  };

  const stats = [
    {
      label: 'Total Records',
      value: counts.totalRecords,
      icon: 'bi-file-earmark-text',
      gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      link: '/records'
    },
    {
      label: 'Active Projects',
      value: counts.activeProjects,
      icon: 'bi-folder',
      gradient: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
      link: '/projects'
    },
    {
      label: 'Pending Tenders',
      value: counts.pendingTenders,
      icon: 'bi-file-earmark-check',
      gradient: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
      link: '/tenders'
    },
    {
      label: 'Active Quotations',
      value: counts.activeQuotations,
      icon: 'bi-file-earmark-text',
      gradient: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
      link: '/quotations'
    },
    {
      label: 'Stock Items',
      value: counts.stockItems,
      icon: 'bi-box-seam',
      gradient: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
      link: '/stock'
    },
    {
      label: 'Organizations',
      value: counts.organizations,
      icon: 'bi-building',
      gradient: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
      link: '/organizations'
    },
  ];

  const barData = {
    labels: barChartData.labels,
    datasets: [
      {
        label: 'Records Created',
        data: barChartData.records,
        backgroundColor: 'rgba(102, 126, 234, 0.9)',
        borderRadius: 8,
        borderSkipped: false,
      },
      {
        label: 'Projects Started',
        data: barChartData.projects,
        backgroundColor: 'rgba(118, 75, 162, 0.9)',
        borderRadius: 8,
        borderSkipped: false,
      },
    ],
  };

  const barOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        titleFont: {
          size: 14,
          weight: '600'
        },
        bodyFont: {
          size: 13
        },
        cornerRadius: 8,
        displayColors: true,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
        ticks: {
          font: {
            size: 11
          },
          stepSize: 1
        }
      },
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 11
          }
        }
      }
    },
    animation: {
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  const doughnutData = {
    labels: tenderStatusData.labels,
    datasets: [
      {
        data: tenderStatusData.data,
        backgroundColor: [
          'rgba(16, 185, 129, 0.9)', // Draft (Greenish?) Maybe change
          'rgba(59, 130, 246, 0.9)', // Submitted
          'rgba(245, 158, 11, 0.9)', // Evaluated
          'rgba(16, 185, 129, 0.9)', // Awarded
          'rgba(239, 68, 68, 0.9)', // Not Awarded
          'rgba(107, 114, 128, 0.9)', // Cancelled
        ],
        // Adjust colors to match labels roughly if needed, purely aesthetic
        borderWidth: 0,
        hoverOffset: 8,
      },
    ],
  };

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: 12,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
      }
    },
    animation: {
      animateRotate: true,
      animateScale: true,
      duration: 1500,
      easing: 'easeInOutQuart'
    }
  };

  // Mock data for line chart as we don't have stock history backend yet
  const lineData = {
    labels: ['Week 1', 'Week 2', 'Week 3', 'Week 4'],
    datasets: [
      {
        label: 'Stock Inflow',
        data: [100, 150, 120, 180], // Remains mock
        borderColor: 'rgb(102, 126, 234)',
        backgroundColor: 'rgba(102, 126, 234, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(102, 126, 234)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
      {
        label: 'Stock Outflow',
        data: [80, 120, 100, 150], // Remains mock
        borderColor: 'rgb(118, 75, 162)',
        backgroundColor: 'rgba(118, 75, 162, 0.1)',
        borderWidth: 3,
        fill: true,
        tension: 0.4,
        pointRadius: 6,
        pointHoverRadius: 8,
        pointBackgroundColor: 'rgb(118, 75, 162)',
        pointBorderColor: '#fff',
        pointBorderWidth: 2,
      },
    ],
  };

  const lineOptions = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: 12,
            weight: '600'
          },
          padding: 15,
          usePointStyle: true,
        }
      },
      tooltip: {
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        padding: 12,
        cornerRadius: 8,
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(0, 0, 0, 0.05)',
        },
      },
      x: {
        grid: {
          display: false,
        }
      }
    },
    animation: {
      duration: 2000,
      easing: 'easeInOutQuart'
    }
  };

  return (
    <div className="animate-fade-in">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1" style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            fontWeight: '700',
            fontSize: '2rem'
          }}>
            Dashboard
          </h2>
          <p className="text-muted mb-0">Welcome back! Here's what's happening today.</p>
        </div>
      </div>

      <div className="row g-4 mb-4">
        {stats.map((stat, index) => (
          <div key={index} className="col-6 col-md-4 col-lg-2">
            <Link to={stat.link} className="text-decoration-none">
              <div
                className="card stat-card text-white h-100"
                style={{
                  background: stat.gradient,
                  backgroundSize: '200% 200%',
                  animation: `gradientShift 3s ease infinite, fadeIn 0.6s ease-out ${index * 0.1}s both`
                }}
              >
                <div className="card-body p-3">
                  <div className="d-flex justify-content-between align-items-start">
                    <div className="flex-grow-1">
                      <h6 className="card-title mb-2" style={{ fontSize: '0.75rem', opacity: 0.9, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                        {stat.label}
                      </h6>
                      <h3 className="mb-0" style={{ fontSize: '1.75rem', fontWeight: '700' }}>
                        {loading ? '...' : stat.value}
                      </h3>
                    </div>
                    <i
                      className={`bi ${stat.icon}`}
                      style={{
                        fontSize: '2.5rem',
                        opacity: 0.3,
                        transition: 'all 0.3s ease'
                      }}
                    ></i>
                  </div>
                </div>
              </div>
            </Link>
          </div>
        ))}
      </div>

      <div className="row g-4">
        <div className="col-12 col-md-8 mb-4">
          <div className="card animate-slide-in-left">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-bar-chart me-2"></i>
                Records & Projects Overview
              </h5>
            </div>
            <div className="card-body p-4">
              <Bar data={barData} options={barOptions} />
            </div>
          </div>
        </div>
        <div className="col-12 col-md-4 mb-4">
          <div className="card animate-slide-in-right">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-pie-chart me-2"></i>
                Tender Status Distribution
              </h5>
            </div>
            <div className="card-body p-4">
              <Doughnut data={doughnutData} options={doughnutOptions} />
            </div>
          </div>
        </div>
      </div>

      <div className="row g-4">
        <div className="col-12">
          <div className="card animate-fade-in">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="bi bi-graph-up me-2"></i>
                Stock Movement Trend
              </h5>
            </div>
            <div className="card-body p-4">
              <Line data={lineData} options={lineOptions} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;

